import { commaSeparatedNumber } from '@/lib/math'
import { httpPostJson } from '@/lib/net'
import { metersToFeet, feetToMeters } from '@/components/shared/x-distance.vue'
import { getItem } from '@/plugins/storage'
import { lineString, length } from '@turf/turf'
import type { Position } from 'geojson'

interface ElevationResponse {
  /**
   * An array of coordinates with elevation data. Matches the length of coords
   * array provided to request.
   *
   * Each position will have an X, Y, and Z.
   */
  data: Position[]
  status: string
}

/**
 * Returns a formatted elevation based on user pref metric.
 *
 * @param coords a coordinate
 * @return formatted elevation
 */
export async function getElevationAtCoord(coords: Position[]) {
  const elevationSet = await requestElevation(coords, true)
  const { elevation, unit } = convertElevation(elevationSet[0], 'm')

  return `${commaSeparatedNumber(Math.round(elevation))} ${unit}`
}

/**
 * Request the elevation for a set of coordinates from the elevation service
 *
 * @param coords an array of coordinates
 * @param useExactCoords whether to return the elevation at the exact coords
 * passed in, or to return elevation polled along the path at even intervals
 * @return set of elevations in meters
 */
export async function requestElevation(
  coords: Position[],
  useExactCoords = false
) {
  if (useExactCoords) {
    return await requestElevationAtCoords(coords)
  }

  return await requestElevationAlongPath(coords)
}

/**
 * Request the elevation from v1/elevation/coords which will return
 * the exact elevation at the specific coords
 *
 * @param coords an array of coordinates
 * @return set of elevations in meters
 */
const requestElevationAtCoords = async (coords: Position[]) => {
  let elevation = [0]

  const url = '/v1/elevation/coords'

  // request elevation
  try {
    const response = await httpPostJson<ElevationResponse>(url, {
      coords,
    })
    elevation = response?.data?.map((item) => item[2])
  } catch (e) {
    /* ignore error and use default */
  }

  return elevation
}

/**
 * Request the elevation from v1/elevation/path  which will return
 * the elevation in intervals along the path
 *
 * @param coords an array of coordinates
 * @return set of elevations in meters
 */
const requestElevationAlongPath = async (coords: Position[]) => {
  let elevation = [0]

  const url = '/v1/elevation/path'
  const line = lineString(coords)
  const lengthInMeters = length(line) * 1000
  const resolution = getPathResolution(lengthInMeters)

  // request elevation
  try {
    const response = (await httpPostJson(url, {
      coords,
      resolution,
    })) as ElevationResponse
    elevation = response?.data?.map((item) => item[2])
  } catch (e) {
    /* ignore error and use default */
  }

  return elevation
}

/**
 * Get the resolution to request for the v1/elevation/path endpoint.
 * Resolution determines the # of places between coordinates we poll
 * for elevation
 * @param distance the length of the path in meters
 * @return the resolution to request elevation along the path
 */
export const getPathResolution = (distance: number) => {
  const resolution = distance / 100
  if (resolution >= 80) {
    return 100
  } else if (resolution < 1) {
    return 1
  }
  return resolution
}

/**
 * Convert an elevation value based on user unit preferences
 *
 * @param elevation elevation value
 * @param unit current unit of measure for the elevation value ('m' or 'ft')
 * @returns converted elevation value and value
 */
export function convertElevation(elevation: number, unit: 'm' | 'ft') {
  const prefUnit = getItem('unitType')

  if (unit === 'm' && prefUnit === 'imperial') {
    elevation = metersToFeet(elevation)
    unit = 'ft'
  } else if (unit === 'ft' && prefUnit === 'metric') {
    elevation = feetToMeters(elevation)
    unit = 'm'
  }

  return { elevation, unit }
}
